import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { LearningQueueApi } from 'src/api';
import { LearningFeature, SUPPORTED_LEARNING_FEATURE_TYPES } from 'src/api/learningQueue/types';
import { Lesson, DataRefreshMode } from '../types';
import { useLatestData } from './useLatestData';
import { useConvertedLessons } from './useConvertedLessons';

const isSupportedFeature = (f: LearningFeature) =>
  SUPPORTED_LEARNING_FEATURE_TYPES.includes(f.type);
const toSupportedFeatures = (lf: LearningFeature[]) => lf.filter(isSupportedFeature);

export const useVisibleLessons = ({ enabled }: { enabled: boolean }) => {
  const languagePairId = getLanguagePairId();

  const [dataUpdateKey, setDataUpdateKey] = useState(uuidv4());
  const [forceRefreshKey, setForceRefreshKey] = useState(uuidv4());
  const {
    learningFeatures,
    isLoading: isLoadingLessons,
    isError: isErrorLessons,
  } = useLatestData({ forceRefreshKey, dataUpdateKey, enabled });

  const isTopFeatureUnsupported = !!learningFeatures && !isSupportedFeature(learningFeatures[0]);
  const fetchedLessons = useConvertedLessons(
    learningFeatures && !isTopFeatureUnsupported
      ? toSupportedFeatures(learningFeatures)
      : undefined,
  );
  const [inMemoryLessons, setInMemoryLessons] = useState<Lesson[] | null>(null);
  const lessons = inMemoryLessons || fetchedLessons;

  const isWaitingForSkip = useRef(false);
  const isSkipInProgress = useRef(false);
  const [isSkipError, setIsSkipError] = useState(false);

  const handleDataRefresh = useCallback(
    (mode: DataRefreshMode) => {
      if (mode === 'force') {
        setForceRefreshKey(uuidv4());
      } else {
        setDataUpdateKey(uuidv4());
      }
    },
    [setDataUpdateKey, setForceRefreshKey],
  );

  const handleSkip = useCallback(() => {
    const asyncSkipFn = async () => {
      if (!languagePairId || !learningFeatures || !lessons) return;

      if (isSkipInProgress.current || isWaitingForSkip.current) return;

      setIsSkipError(false);

      if (lessons[0].type !== learningFeatures[0].type) {
        isWaitingForSkip.current = true;
        return;
      }

      isSkipInProgress.current = true;

      try {
        const featuresForSkip = getFeaturesForSkip(learningFeatures);
        await LearningQueueApi.skipLearningQueueFeatures(
          languagePairId,
          featuresForSkip.map(f => f.type),
        );
        const skippedLessonsCount = toSupportedFeatures(featuresForSkip).length;
        setInMemoryLessons(lessons.slice(skippedLessonsCount));
        setDataUpdateKey(uuidv4());
      } catch (_) {
        setIsSkipError(true);
      } finally {
        isSkipInProgress.current = false;
      }
    };

    void asyncSkipFn();
  }, [languagePairId, learningFeatures, lessons]);

  useEffect(() => {
    if (isWaitingForSkip.current) {
      isWaitingForSkip.current = false;
      handleSkip();
    }
  }, [handleSkip]);

  useEffect(() => {
    setInMemoryLessons(null);
  }, [learningFeatures]);

  useEffect(() => {
    if (!isTopFeatureUnsupported) return;

    const skipTopFeature = async () => {
      // istanbul ignore if: should not happen, we have top feature and know that it is unsupported
      if (!languagePairId || !learningFeatures) return;

      try {
        const featuresForSkip = getFeaturesForSkip(learningFeatures);
        await LearningQueueApi.skipLearningQueueFeatures(
          languagePairId,
          featuresForSkip.map(f => f.type),
        );
      } finally {
        handleDataRefresh('wait-for-update');
      }
    };

    void skipTopFeature();
  }, [languagePairId, learningFeatures, isTopFeatureUnsupported, handleDataRefresh]);

  useEffect(() => {
    isWaitingForSkip.current = false;
    setInMemoryLessons(null);
    setIsSkipError(false);
  }, [forceRefreshKey]);

  return {
    lessons: inMemoryLessons || fetchedLessons,
    handleSkip,
    handleDataRefresh,
    isLoading: isLoadingLessons,
    isError: isErrorLessons,
    isSkipError,
  };
};

export function getFeaturesForSkip(
  learningFeatures: LearningFeature[],
  skipOnlyUnsupported?: boolean,
) {
  const featuresForSkip: LearningFeature[] = [];
  const topFeature = learningFeatures[0];

  learningFeatures.every(f => {
    if (!isSupportedFeature(f)) {
      featuresForSkip.push(f);
      return true;
    }

    if (
      skipOnlyUnsupported ||
      f.type !== topFeature.type ||
      (!f.skippable && topFeature.skippable)
    ) {
      return false;
    }

    featuresForSkip.push(f);
    return true;
  });

  return featuresForSkip;
}
